import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { whatsappUrlForCustomerPhone } from '../../utils/whatsappMessage';
import './AdminPages.css';
import './Orders.css';

const STATUS_OPTIONS = [
  { value: 'new', label: 'جديد' },
  { value: 'in_progress', label: 'قيد التنفيذ' },
  { value: 'completed', label: 'مكتمل' },
  { value: 'cancelled', label: 'ملغي' },
];

function statusClass(s) {
  if (s === 'new') return 'order-status order-status--new';
  if (s === 'in_progress') return 'order-status order-status--progress';
  if (s === 'completed') return 'order-status order-status--done';
  if (s === 'cancelled') return 'order-status order-status--cancel';
  return 'order-status';
}

function OrdersList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminAPI.getOrders();
      const data = res.data;
      const rows = Array.isArray(data) ? data : data?.results || [];
      setList(rows);
    } catch (e) {
      setError(e.response?.data?.detail || 'فشل التحميل');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  if (loading) return <div className="spinner" style={{ margin: '48px auto' }} />;
  if (error) {
    return (
      <div className="admin-page">
        <p className="admin-error-banner">{error}</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>الطلبات</h1>
        <button type="button" className="btn btn-secondary" onClick={load}>
          تحديث
        </button>
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>رقم الطلب</th>
              <th>الهاتف</th>
              <th>العنوان</th>
              <th>الحالة</th>
              <th>التاريخ</th>
              <th>تفاصيل</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={6}>لا طلبات بعد</td>
              </tr>
            ) : (
              list.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.order_number}</strong>
                  </td>
                  <td dir="ltr">{o.phone}</td>
                  <td>{o.address || '—'}</td>
                  <td>
                    <span className={statusClass(o.status)}>
                      {STATUS_OPTIONS.find((x) => x.value === o.status)?.label || o.status}
                    </span>
                  </td>
                  <td>{o.created_at ? new Date(o.created_at).toLocaleString('ar-LY') : '—'}</td>
                  <td>
                    <Link to={`/admin/orders/${o.id}`} className="btn btn-primary btn-sm">
                      عرض
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminAPI.getOrder(id);
      setOrder(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'الطلب غير موجود');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatusChange = async (e) => {
    const status = e.target.value;
    setSaving(true);
    try {
      await adminAPI.patchOrderStatus(id, status);
      setOrder((prev) => (prev ? { ...prev, status } : null));
    } catch (err) {
      alert(err.response?.data?.detail || 'فشل التحديث');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="spinner" style={{ margin: '48px auto' }} />;
  if (error || !order) {
    return (
      <div className="admin-page">
        <p>{error || '—'}</p>
        <Link to="/admin/orders">العودة للقائمة</Link>
      </div>
    );
  }

  const wa = whatsappUrlForCustomerPhone(order.phone);

  return (
    <div className="admin-page order-detail-page">
      <div className="admin-page-header">
        <div>
          <Link to="/admin/orders" className="order-back-link">
            ← الطلبات
          </Link>
          <h1>طلب {order.order_number}</h1>
        </div>
      </div>

      <div className="order-detail-meta">
        <div className="order-detail-row">
          <span className="order-detail-label">الهاتف</span>
          <span dir="ltr">{order.phone}</span>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp-admin">
              واتساب
            </a>
          )}
        </div>
        <div className="order-detail-row">
          <span className="order-detail-label">العنوان</span>
          <span>{order.address || '—'}</span>
        </div>
        <div className="order-detail-row">
          <span className="order-detail-label">التاريخ</span>
          <span>{order.created_at ? new Date(order.created_at).toLocaleString('ar-LY') : '—'}</span>
        </div>
        <div className="order-detail-row order-detail-row--status">
          <span className="order-detail-label">الحالة</span>
          <select
            className="order-status-select"
            value={order.status}
            onChange={onStatusChange}
            disabled={saving}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className={statusClass(order.status)}>
            {STATUS_OPTIONS.find((x) => x.value === order.status)?.label}
          </span>
        </div>
      </div>

      <h2 className="order-detail-section-title">المنتجات حسب السيارة</h2>
      {(order.items_by_vehicle || []).map((g, idx) => (
        <section key={idx} className="order-vehicle-block">
          <h3 className="order-vehicle-title">
            {g.model_name || '—'} {g.variant_name ? `— ${g.variant_name}` : ''}
          </h3>
          <ul className="order-items-list">
            {(g.items || []).map((it) => (
              <li key={it.id} className="order-item-line">
                <strong>{it.product_name}</strong>
                {it.part_number && <span className="order-item-meta"> رقم: {it.part_number}</span>}
                <span className="order-item-meta"> ×{it.quantity}</span>
                {it.unit_price != null && (
                  <span className="order-item-meta"> {it.unit_price} د.ل</span>
                )}
                {it.image_url && (
                  <div className="order-item-img-wrap">
                    <a href={it.image_url} target="_blank" rel="noopener noreferrer">
                      صورة
                    </a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {order.whatsapp_text && (
        <details className="order-whatsapp-raw">
          <summary>نص رسالة واتساب (مرجع)</summary>
          <pre>{order.whatsapp_text}</pre>
        </details>
      )}
    </div>
  );
}

export default function Orders() {
  const { id } = useParams();
  if (id) return <OrderDetail />;
  return <OrdersList />;
}
